/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { BasicLayout, OrientationMap, Orientor } from '@1x2network/layout'
import {
    applySpecification,
    ContainerSpecification,
    createSprite,
    Specification,
    SpriteSpecification,
} from '@1x2network/pixi_assets'
import { SplashComponent } from '@1x2network/pixi_splash'
import { GraphicMask, TradReelSet } from '@1x2network/reel_set'
import gsap from 'gsap'
import { Container, IDestroyOptions } from 'pixi.js'
import { valueIds } from '../../constants/valueIds'
import { CustomSymbol, CustomSymbolSpecification } from '../../CustomSymbol'
import { SymbolModel } from '../../model/SymbolModel'
import { tweenTint } from '../../utils/spine'
import { CustomSymbolPool } from '../CustomSymbolPool'
import { ReelFrame } from '../ReelFrame'

/**
 * A SplashComponent that shows a preview of a spinning reel set landing a Hold and Win trigger board
 * @author Adam Drake - <adam.drake@1x2network.com>
 * @version 1.0.0
 */
export class HoldAndWinLoop extends SplashComponent<HoldAndWinLoopSpecification> {
    private reelSet: TradReelSet<CustomSymbol, SymbolModel>
    private timeline?: GSAPTimeline
    private orientor: Orientor<ContainerSpecification>

    constructor(options: { parent?: Container; layout: BasicLayout }) {
        super({ spec: 'holdAndWinLoop', parent: options.parent })

        const pool = new CustomSymbolPool(Specification.get('symbols') as CustomSymbolSpecification[], CustomSymbol)
        this.reelSet = new TradReelSet<CustomSymbol, SymbolModel>({ spec: 'reelSet', parent: this, pool })
        new GraphicMask({ spec: 'reelSetMask', reelSet: this.reelSet.spinContainer })
        createSprite({
            spec: Specification.get('reelBackground') as SpriteSpecification,
            parent: this.reelSet,
        })
        new ReelFrame('reelFrame', this.reelSet)

        this.prepareSpin()

        this.orientor = new Orientor({
            layout: options.layout,
            property: this.specification,
            onOrient: (active) => {
                applySpecification(this, active)
            },
        })
    }

    private prepareSpin(): void {
        this.reelSet.prepareSpin([
            [{ id: 0 }, { id: 0 }, { id: 0 }],
            [{ id: 0 }, { id: 0 }, { id: 0 }],
            [{ id: 0 }, { id: 0 }, { id: 0 }],
            [{ id: 0 }, { id: 0 }, { id: 0 }],
            [{ id: 0 }, { id: 0 }, { id: 0 }],
        ])
    }

    public show(): void {
        super.show()

        this.animate()
    }

    public hide(): void {
        super.hide()

        this.timeline?.kill()

        this.reelSet.cancelSpin()
        this.prepareSpin()
    }

    private animate() {
        this.timeline = gsap
            .timeline({ repeat: -1, repeatDelay: 1.5 })

            // Spin
            .call(() => {
                this.reelSet.startSpin()
            })

            // Stop spinning
            .call(
                () => {
                    this.reelSet.stopSpin({ symbols: stopSpinResult })
                },
                [],
                '+=1.5',
            )

            // Animate HAW symbols, fade out non-winning symbols
            .call(
                () => {
                    const fade = this.reelSet.fetcher
                        .getSymbolsNotInPositions([
                            [0, 0],
                            [1, 1],
                            [2, 1],
                            [2, 2],
                            [4, 1],
                            [4, 2],
                        ])
                        .map((s) => s.spine!)

                    tweenTint(fade, 0x7f7f7f)
                    valueIds
                        .map((id) => this.reelSet.fetcher.getSymbolsById(id))
                        .flat()
                        .forEach((symbol) => {
                            symbol.playWin()
                        })
                },
                [],
                '+=1.1',
            )

            // Reset non-winning symbols
            .call(
                () => {
                    const fade = this.reelSet.fetcher
                        .getSymbolsNotInPositions([
                            [0, 0],
                            [1, 1],
                            [2, 1],
                            [2, 2],
                            [4, 1],
                            [4, 2],
                        ])
                        .map((s) => s.spine!)
                    tweenTint(fade, 0xffffff)
                },
                [],
                '+=1',
            )
    }

    public destroy(options?: IDestroyOptions | boolean): void {
        this.orientor.disconnect()
        super.destroy(options)
    }
}

const stopSpinResult: SymbolModel[][] = [
    [
        {
            id: 9,
            timesStake: { start: 1, end: 1 },
            cash: {
                start: { currency: 1, unit: 100 },
                end: { currency: 1, unit: 100 },
            },
        },
        { id: 3 },
        { id: 5 },
    ],
    [
        { id: 6 },
        {
            id: 9,
            timesStake: { start: 2, end: 2 },
            cash: {
                start: { currency: 2, unit: 200 },
                end: { currency: 2, unit: 200 },
            },
        },
        { id: 7 },
    ],
    [
        { id: 4 },
        {
            id: 10,
            timesStake: { start: 0, end: 0 },
            cash: {
                start: { currency: 0, unit: 0 },
                end: { currency: 0, unit: 0 },
            },
        },
        {
            id: 9,
            timesStake: { start: 5, end: 5 },
            cash: {
                start: { currency: 5, unit: 500 },
                end: { currency: 5, unit: 500 },
            },
        },
    ],
    [{ id: 2 }, { id: 1 }, { id: 4 }],
    [
        { id: 1 },
        {
            id: 9,
            timesStake: { start: 2, end: 1 },
            cash: {
                start: { currency: 1, unit: 100 },
                end: { currency: 1, unit: 100 },
            },
        },
        {
            id: 11,
            timesStake: { start: 20, end: 20 },
            cash: {
                start: { currency: 20, unit: 2000 },
                end: { currency: 20, unit: 2000 },
            },
        },
    ],
]

type ContainerSpecWithOrientation = ContainerSpecification & OrientationMap<ContainerSpecification>

export type HoldAndWinLoopSpecification = ContainerSpecWithOrientation
